import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Phone, Mail, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import ChaseDialog from "@/components/ChaseDialog";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function LateFeeAlerts() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [pending, setPending] = useState(null);
  const [chase, setChase] = useState(null);

  const load = () => api.get("/late-fees").then(r => setData(r.data)).catch(() => setErr(true));
  useEffect(() => { load(); }, []);

  const markFiled = (a) => setPending(a);
  const confirmFiled = async () => {
    const a = pending; setPending(null);
    try {
      await api.post("/late-fees/mark-filed", { client_id: a.client_id, form: a.form, due_date: a.due_date });
      toast.success("Marked as filed", { description: `${a.client_name} · ${a.form}` });
      load();
    } catch (e) { toast.error("Failed", { description: e.response?.data?.detail || e.message }); }
  };

  if (err || !data) return null;
  const rows = showAll ? data.alerts : data.alerts.slice(0, 6);

  return (
    <section data-testid="panel-late-fees" className={`bg-white border p-6 ${data.count ? "border-rose-200" : "border-zinc-200"}`}>
      <header className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <AlertTriangle size={16} strokeWidth={1.75} className={data.count ? "text-rose-600" : "text-zinc-900"} />
          <h2 className="font-heading font-bold tracking-tight text-lg">Missed filings & late-fee exposure</h2>
        </div>
        {data.count ? (
          <div className="text-xs text-zinc-600" data-testid="late-fee-total">
            <b className="tabular text-rose-700">{data.count}</b> overdue · est. <b className="tabular text-rose-700">{inr(data.total_estimated)}</b>
          </div>
        ) : null}
      </header>

      {!data.count ? (
        <div className="text-sm text-zinc-500" data-testid="late-fee-empty">No missed statutory filings. Every past due date has a completed task.</div>
      ) : (
        <>
          <ul className="divide-y divide-zinc-100" data-testid="late-fee-list">
            {rows.map((a, i) => (
              <li key={`${a.client_id}-${a.form}-${a.due_date}`} data-testid={`late-fee-item-${i}`} className="py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <Link to={`/clients?q=${encodeURIComponent(a.client_name)}`} className="font-semibold text-sm truncate hover:underline">{a.client_name}</Link>
                    <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 bg-rose-50 text-rose-700 border border-rose-200 whitespace-nowrap">
                      {a.days_overdue}d overdue
                    </span>
                  </div>
                  <div className="text-xs text-zinc-600 truncate">{a.form} · {a.period} · due {a.due_date}</div>
                  <div className="text-[11px] text-zinc-600 truncate" title={a.fee_note}>{a.fee_note}</div>
                </div>
                <div className="flex items-center gap-3 sm:gap-4 shrink-0">
                  <div className="text-right">
                    <div className="font-heading font-bold tabular text-rose-700">{a.estimated_fee ? inr(a.estimated_fee) : "—"}</div>
                    <div className="text-[11px] uppercase tracking-wider text-zinc-600">
                      {a.task_id ? <Link to="/tasks" className="hover:underline">{a.task_status}{a.assignee ? ` · ${a.assignee}` : ""}</Link> : "No task"}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {a.phone ? <a data-testid={`late-fee-call-${i}`} href={`tel:${a.phone}`} className="p-1.5 border border-zinc-200 hover:bg-zinc-900 hover:text-white transition-colors" title={a.phone}><Phone size={13} /></a> : null}
                    <button data-testid={`late-fee-chase-${i}`} onClick={() => setChase(a)} className="px-2 py-1.5 border border-zinc-900 bg-zinc-900 text-white text-[11px] font-semibold hover:bg-zinc-700 transition-colors flex items-center gap-1" title="Send reminder with fee estimate">
                      <Mail size={12} /> Chase
                    </button>
                    {isAdmin ? <button data-testid={`late-fee-filed-${i}`} onClick={() => markFiled(a)} className="p-1.5 border border-zinc-200 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-colors" title="Mark as filed"><CheckCircle2 size={13} /></button> : null}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          {data.count > 6 ? (
            <button data-testid="late-fee-toggle" onClick={() => setShowAll(v => !v)} className="mt-3 text-[11px] tracking-[0.14em] uppercase font-semibold text-zinc-500 hover:text-zinc-900">
              {showAll ? "Show less" : `Show all ${data.count}`}
            </button>
          ) : null}
          <p className="mt-3 text-[11px] text-zinc-500">Estimates are indicative statutory fees; actual amounts depend on turnover, tax due and nil-return status.</p>
        </>
      )}
      <ChaseDialog alert={chase} onClose={() => setChase(null)} onSent={load} />
      <AlertDialog open={!!pending} onOpenChange={(o) => { if (!o) setPending(null); }}>
        <AlertDialogContent data-testid="mark-filed-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">Mark as filed?</AlertDialogTitle>
            <AlertDialogDescription>
              {pending ? `${pending.form} (${pending.period}) for ${pending.client_name} will be recorded as a completed filing and removed from this list.` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mark-filed-cancel" className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="mark-filed-confirm" onClick={confirmFiled} className="rounded-none bg-zinc-900 hover:bg-zinc-800">Mark filed</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
